import type { Articolo, OrdineArticolo, RowItem, TableItem } from "@/types";
import { isFunction, isNil, isNull, isUndefined } from "lodash";
import { createApp, h } from "vue";
import { BModal } from "bootstrap-vue-next";
import type { BvTriggerableEvent } from "bootstrap-vue-next";
import type { OrdineStore } from "@/stores/ordini.types";
import { notify } from "@kyvg/vue3-notification";
import axios, { type AxiosResponse } from "axios";


export type Dict<T> = { [key: string]: T };
export {
  isBoolean,
  isString,
  isFunction,
  isNull,
  isNumber,
  isUndefined,
  isDate,
  isNaN,
  isObject,
  isArray,
  orderBy,
  isNil,
} from "lodash";


export async function loadConfigs(): Promise<AxiosResponse[]> {
  let requests: Promise<AxiosResponse>[];
  if (import.meta.env.PROD) {
    requests = [
      axios.get("./config.json", { validateStatus: () => true }),
      axios.get("./config.production.json", { validateStatus: () => true })
    ];
  } else {
    requests = [
      //axios.get("./config.json", { validateStatus: () => true }),
      axios.get("./config.development.json", { validateStatus: () => true })
    ];
  }
  return (await Promise.all(requests)).filter((r) => r.status != 404);
}

export function isDefined(value: unknown) {
  return !isUndefined(value);
}

export function isNilOrEmpty(value: string | undefined | null): boolean {
  return isNil(value) || !value?.trim();
}

export function isDivisible(num: number, step: number): boolean {
  return (num / step) % 1 === 0;
}

export const compareStrings = (str: string | null, src: string | null | boolean): boolean => {
  if (!str && src) return false;
  if (str && !src) return true;
  if (src === true || src === false) src = "";
  return `${str}`.toLowerCase().indexOf(`${src}`.toLowerCase()) !== -1;
};

export const toType = (value: unknown) => typeof value;
export const toFloat = (value: unknown, defaultValue = NaN) => {
  const float = parseFloat(value as never);
  return isNaN(float) ? defaultValue : float;
};
export const isNumeric = (value: unknown): boolean => /^[0-9]*\.?[0-9]+$/.test(String(value));
export const isUndefinedOrNull = (value: unknown) => isUndefined(value) || isNull(value);

/**
 * riduce gli attributi di un oggetto a quelli specificati nel parametro `keys`
 * rimuovendo tutti quelli non specificati
 * @param dict oggetto da modificate
 * @param keys lista degli attributi che comporranno l'oggetto restituiro
 */
export function subDict<T>(dict: Dict<T>, keys: string[]): Dict<T> {
  if (!keys) return {} as Dict<T>;
  if (!keys.length) return {} as Dict<T>;
  return keys.reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (obj: any, key) =>
      Object.assign(
        {
          [key]: dict[key],
        },
        obj
      ),
    {}
  );
}

/**
 * restituisce un dictionary senza le chiavi passate nell'array notKeys
 * @param dict
 * @param notkeys
 */
export function subDictNeg<T>(dict: Dict<T>, notkeys: string[]): Dict<T> {
  if (!notkeys) return {} as Dict<T>;
  if (!notkeys.length) return dict;
  const result: Dict<T> = {};
  for (const [key, value] of Object.entries(dict)) {
    if (!notkeys.includes(key)) {
      result[key] = value;
    }
  }
  return result;
}

export type Fn<T, K> = (d: T) => K;

/**
 * indicizzazione di un array di object ( naive )
 * trasforma un array di oggetti in un oggetto con
 * chiave: il valore dell'attributo dell'oggetto specificato nel parametro `indexName`
 * valore: l'oggetto stesso
 *
 * è possibile ridurre gli attributi degli aggetti del dictionary restitutito a quelli specificati nel parametri `keys`
 *
 * @param arr
 * @param indexName nome dell'attributo che valorizzerà le chiavi dell'oggetto restituito oppure funzione che ritorna la chiave per l'oggetto passato
 * @param keys (opzionale) lista degli attributi che i valori del dictionary restituito dovrà contenere
 */
export function indexArray<T, K extends keyof T>(arr: T[], indexName: K | Fn<T, T[K]>, keys?: string[]): Dict<T> {
  if (!arr || !arr.length) {
    return {};
  }
  return arr.reduce((cumm: Dict<T>, r: T) => {
    let key_val: T[K];
    if (isFunction(indexName)) {
      key_val = (indexName as Fn<T, T[K]>)(r);
    } else {
      key_val = r[indexName as K];
    }
    if (keys) {
      cumm[`${key_val}`] = subDict<never>(r as never, keys) as T;
    } else {
      cumm[`${key_val}`] = r;
    }
    return cumm;
  }, {});
}

export function indexArrayNeg<T, K extends keyof T>(arr: T[], indexName: K | Fn<T, T[K]>, notKeys?: string[]): Dict<T> {
  if (!arr || !arr.length) {
    return {};
  }
  const ret = arr.reduce((cumm: Dict<T>, r: T) => {
    let key_val: T[K];
    if (isFunction(indexName)) {
      key_val = (indexName as Fn<T, T[K]>)(r);
    } else {
      key_val = r[indexName as K];
    }
    if (notKeys) {
      cumm[`${key_val}`] = subDictNeg<never>(r as never, notKeys) as T;
    } else {
      cumm[`${key_val}`] = r;
    }
    return cumm;
  }, {});
  console.log("indexArrayNeg", arr, indexName, notKeys, ret);
  return ret
}

export const floatFormatter = <T>(value: T): string => {
  // eh si lo so è uno schifo ma risolve il problema del floating point rimuvendo comunque gli zero dopo la virgole
  return `${parseFloat(floatFormattertoFixed(value))}`;

};

export const floatFormattertoFixed = <T>(value: T, digits: number = 3): string => {
  if (isUndefinedOrNull(value)) return "0";
  return `${parseFloat(String(value)).toFixed(digits)}`;
};

/**
 * genera una funzione che formatta un numero come valuta
 * @param {string} currency
 */
export const currencyFormatter = <T>(currency: string = "EUR") => (value: T /*, key: string, item: any*/): string => {
  const options : Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currency,
    minimumIntegerDigits: 1,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  const val = parseFloat(String(value));
  if (isNaN(val as number)) return "0";
  return val.toLocaleString('it-IT', options)
};

export function formatDate(value: unknown | Date): string {
  if (!value) return "";
  const date = value instanceof Date ? value : parseDate(`${value}`, null);
  if (date instanceof Date) {
    const format = new Intl.DateTimeFormat(
      "it-IT",
      { year: "numeric", month: "2-digit", day: "2-digit" }
    );
    return format.format(date);
  }
  return String(value)
}

export const percentageFormatter = <T>(value: T): string => `${floatFormatter<T>(value)}%`;

/**
 * coverte il parametro passato in numero se possibile altrimenti 0
 * @param value
 */
export function intFormatter<T>(value: T): number {
  const num = parseInt(String(value));
  if (isNaN(num)) return 0;
  return num;
}

/**
 * ritorna la rappresentazione in stringa nel valore passato convertito in intero ( oppure "0" se non convertibile )
 * @param value
 */
export function intFormatterS(value: unknown): string {
  return String(intFormatter(value));
}

export function dateToParam(value: Date | null): string | null {
  if (!value) return null;
  return value.toISOString().split("T")[0];
}

/**
 * unisce due dictionary in uno, equivalente di una left join con condizione:
 *     objArr1 LEFT JOIN objArr2 ON objArr1[key1] = objArr2[key2]
 * @param objArr1
 * @param objArr2
 * @param key1
 * @param key2
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function leftJoin(objArr1: Dict<any>[], objArr2: Dict<any>[], key1: string, key2: string): Dict<any>[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return objArr1.map((anObj1: Dict<any>) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...objArr2.find((anObj2: Dict<any>) => anObj1[key1] === anObj2[key2]),
    ...anObj1,
  }));
}

export function leftJoinIndex<T, V>(leftObj: Dict<T>, rightObj: Dict<V>): (T & V)[] {
  const result: (T & V)[] = [];
  for (const [key, lObj] of Object.entries(leftObj)) {
    const rObj: V = rightObj[key];
    result.push({ ...lObj, ...rObj } as T & V);
  }
  return result;
}

async function createBModal(isConfirm: boolean, message: string, title: string | null = null): Promise<boolean> {
  const wrapper = document.createElement("div");
  return new Promise((confirm) => {
    const ComponentApp = createApp({
      setup() {
        return () =>
          h(
            BModal,
            {
              okOnly: !isConfirm,
              modelValue: true,
              onOk: () => {
                if (isConfirm) confirm(true);
              },
              onCancel: () => {
                if (isConfirm) confirm(false);
              },
              onHidden: (event: BvTriggerableEvent) => {
                if (!isConfirm) confirm(true);
                // rimuove il div usato per montare il componente
                document.getElementById("app")?.removeChild(wrapper);
                // rimuove il componente teleportato nel body
                if (event.target) {
                  document.body.removeChild(event.target as HTMLElement);
                }
              },
            },
            {
              title: () => title ?? "",
              default: () => message,
            }
          );
      },
    });

    ComponentApp.mount(wrapper);
    document.getElementById("app")?.appendChild(wrapper);
  });
}

export async function modalDialog(message: string, title: string | null = null): Promise<void> {
  await createBModal(false, message, title);
}

export async function modalConfirm(message: string, title: string | null = null): Promise<boolean> {
  return await createBModal(true, message, title);
}

/*
export function useCustomLocalStore<T>(getKey: () => string, initialValue: MaybeComputedRef<T>): RemovableRef<T> {
  const _ls: { [key: string]: RemovableRef<T> } = {};
  const getLs = (): RemovableRef<T> => {
    const key = getKey();
    if (!_ls[key]) {
      _ls[key] = useLocalStorage<T>(getKey(), initialValue, {
        deep: true,
        listenToStorageChanges: true,
      });
    }
    return _ls[key];
  };

  return new Proxy({} as RemovableRef<T>, {
    get(target: RemovableRef<T>, p: keyof RemovableRef<T> ): T | true {
      //console.log("[useNamedLocalStorage] get", getKey(), p);
      const value = p === "value" ? getLs()[p] : getLs()[p];
      if (value == undefined) {
        return initialValue as T | true;
      }
      return value;
    },
    set(target: RemovableRef<T>, p: keyof RemovableRef<T>, value: T & true ): boolean {
      try {
        console.log("[useNamedLocalStorage] set", p, value);
        getLs()[p] = value;
        return true;
      } catch (error) {
        // eslint-disable-next-line no-debugger
        debugger;
        console.error("[useNamedLocalStorage] set error", error, target, p, value);
        return false;
      }
    },
    apply(target: RemovableRef<T>, thisArg: any, argArray: any[]): any {
      console.log("[useNamedLocalStorage] apply", target, thisArg, argArray);
      return {};
    },
    construct(target: RemovableRef<T>, argArray: any[], newTarget: () => any): object {
      console.log("[useNamedLocalStorage] construct", target, argArray, newTarget);
      return {};
    },
    defineProperty(target: RemovableRef<T>, p: keyof RemovableRef<T>, attributes: PropertyDescriptor): boolean {
      console.log("[useNamedLocalStorage] defineProperty", target, p, attributes);
      return true;
    },
    deleteProperty(target: RemovableRef<T>, p: keyof RemovableRef<T>): boolean {
      console.log("[useNamedLocalStorage] deleteProperty", target, p);
      return true;
    },

    getOwnPropertyDescriptor(target: RemovableRef<T>, p: keyof RemovableRef<T>): PropertyDescriptor | undefined {
      console.log("[useNamedLocalStorage] getOwnPropertyDescriptor", target, p);
      return target[p] as PropertyDescriptor;
    },

    has(target: RemovableRef<T>, p: string | symbol): boolean {
      console.log("[useNamedLocalStorage] has", target, p);
      return true;
    },
    isExtensible(target: RemovableRef<T>): boolean {
      console.log("[useNamedLocalStorage] isExtensible", target);
      return true;
    },
    ownKeys(target: RemovableRef<T>): ArrayLike<string | symbol> {
      console.log("[useNamedLocalStorage] ownKeys", target);
      return ["value"];
    },
    preventExtensions(target: RemovableRef<T>): boolean {
      console.log("[useNamedLocalStorage] preventExtensions", target);
      return true;
    },

    setPrototypeOf(target: RemovableRef<T>, v: object | null): boolean {
      console.log("[useNamedLocalStorage] setPrototypeOf", target, v);
      return true;
    },
  });
}

export function useNamedLocalStorage<T>(name: string, initialValue: MaybeComputedRef<T>): RemovableRef<T> {
  const getKey = (): string => {
    const rootStore = useRootStore();
    const customerStore = useCustomerStore();
    return `isales/${name}/${rootStore.VECCDAGE}-${customerStore.VECCDCLF}-${customerStore.VECCDDES}`;
  };
  return useCustomLocalStore(getKey, initialValue);
}
*/

/*
import storage from "./storage";
export function useNamedLocalStorage<T extends string | number | boolean | object | null>(
  name: string,
  initialValue: MaybeComputedRef<T>
): RemovableRef<T> {
  const getKey = (): string => {
    const rootStore = useRootStore();
    const customerStore = useCustomerStore();
    const ordiniStore = useOrdiniStore();
    return `isales/${name}/${ordiniStore.TIPO_ORDINE}-${rootStore.VECCDAGE}-${customerStore.VECCDCLF}-${customerStore.VECCDCLF}`;
  };
  const getLs = (): RemovableRef<T> => {
    const data = {
      get value(): T {
        const key = getKey();
        const value = storage.getObj<T>(key, initialValue as T) as T;
        console.log("read", key, value);
        return value;
      },
      set value(value: T) {
        const key = getKey();
        console.log("write", key, value);
        storage.setObj(key, value);
      },
    };

    return data as RemovableRef<T>;
  };
  return getLs() as RemovableRef<T>;
}
*/

export function formatDescription(
  value: unknown,
  key?: string,
  item?: OrdineArticolo | Articolo | Record<string, unknown>
): string {
  if (item && item.vecswinesaurimento) return `${value} *`;
  return value ? `${value}` : "";
}

export function notif(text: string, title = "", type: "info" | "warn" | "error" = "info"): void {
  notify({
    text,
    title,
    type,
    closeOnClick: true,
  });
}

export function toast(text: string) {
  // TODO change position of this notification
  notify({
    text,
  });
}

export function rowHasErrors(
  ordiniStore: OrdineStore,
  idDoc: string,
  item: TableItem<OrdineArticolo, number>
): boolean | string {
  if (!item.item) {
    return "error";
  }
  const rigaOrdine = ordiniStore.ORDINE_getRiga(idDoc, item.item.artkey);

  if (rigaOrdine?.errors && rigaOrdine.errors[item.field.key]) {
    return rigaOrdine.errors[item.field.key];
  }
  return true;
}

export function applyVariants(articoli: OrdineArticolo[]): RowItem<OrdineArticolo>[] {
  return articoli.map((row: RowItem<OrdineArticolo>) => {
    if (row.vecswinesaurimento) {
      row._cellVariants = { veccdart: "danger", vecdsart: "danger" };
    }
    if (row.aggiunto) {
      row._cellVariants = { veccdart: "success", vecdsart: "success" };
    }
    return row;
  });
}

/*
export function filtraColonneTabella(
  allFields: TableFieldParam<any, any>[],
  tipoOrdine: TipoOrdine,
  mostraRiservati: boolean
): TableFieldParam<OrdineArticolo, any>[] {
  const settingsStore = useSettingsStore();
  const fieldsNames = settingsStore.COLONNE_TABELLA_ORDINE_get(tipoOrdine, mostraRiservati);
  let _fields = allFields.filter((col) => fieldsNames.includes(col.key));
  if (!mostraRiservati) _fields = _fields.filter((e) => e.key !== "vecpzunilis");
  return _fields;
}
*/

export function getItemkey(articolo: { veccdart: string; veccdlotto: string }): string {
  return `${articolo.veccdart ?? ""}-${articolo.veccdlotto ?? ""}`;
}

export function parseDate(dateStr: string, defaultVal: unknown): Date | unknown {
  const dateParser = /(\d{2})[/-](\d{2})[/-](\d{4})( (\d{2}):(\d{2}):(\d{2}))?/;
  const match = dateStr.match(dateParser);
  if (match && !isNaN(parseInt(match[2]))) {
    return new Date(
      Number(match[3]), // year
      parseInt(match[2]) - 1, // monthIndex
      Number(match[1]), // day
      //Number(match[4]) ?? 0, // hours
      //Number(match[5]) ?? 0, // minutes
      //Number(match[6]) ?? 0 //seconds
    );
  } else {
    return defaultVal;
  }
}

export enum ColType {
  FLOAT = "float",
  INTEGER = "integer",
  TEXT = "text",
  LONG_TEXT = "long-text",
  DATE = "date",
  CURRENCY = "currency",
  CURRENCY_SC = "currency_sc",
  CURRENCY_SC_U = "currency_sc_u",
  BANCALI = "bancali",
  CONFEZIONI = "confezioni",
  OMAGGIO = "omaggio",
  OMAGGIO_PEZZI = "omaggio_pezzi",
  PUBBLICITA = "pubblicita",
  PUBBLICITA_PEZZI = "pubblicita_pezzi",
  PEZZI = "pezzi",
  EDITABLE = "editable",
  PERCENTAGE = "percentage",
}


/**
 * Dynamically get a nested value from an array or
 * object with a string.
 *
 * @example get(person, 'friends[0].name')
 * @link https://github.com/rayepps/radash/blob/master/src/object.ts#L214
 */
export const get = <TDefault = unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  path: string,
  defaultValue?: TDefault
): TDefault => {
  const segments = path.split(/[.[\]]/g)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = value
  for (const key of segments) {
    if (current === null) return defaultValue as TDefault
    if (current === undefined) return defaultValue as TDefault
    if (key.trim() === '') continue
    current = current[key]
  }
  if (current === undefined) return defaultValue as TDefault
  return current
}

export class HashTable<K, V> {
  _hashes: { [key: string]: V };

  constructor() {
    this._hashes = {};
  }

  put(key: K, value: V) {
    this._hashes[JSON.stringify(key)] = value;
  }

  get(key: K): V | undefined {
    return this._hashes[JSON.stringify(key)];
  }

  has(key: K) {
    return this.get(key) !== undefined;
  }

  * iterate(): Generator<[K, V]> {
    for (const key in this._hashes) {
      yield [JSON.parse(key), this._hashes[key]];
    }
  }

  size(): number {
    return Object.keys(this._hashes).length;
  }

  forEach(callback: (value: V, key: K) => void) {
    for (const key in this._hashes) {
      const realKey = JSON.parse(key);
      callback(this._hashes[key], realKey);
    }
  }
}

export function executeCalc(calc: string, params: Dict<unknown>, params2: Dict<unknown>|null = null): string | number {
  const keys= Object.keys(params);
  const values = keys.map(key => params[key]);
  if (params2) {
    const keys2 = Object.keys(params2);
    const values2 = keys2.map(key => params2[key]);
    keys.push(...keys2);
    values.push(...values2);
  }
  const func = new Function(...keys, `return (${calc})`);
  console.debug(`executeCalc '${calc}' with`, keys, values, func);
  return func(...values);
}


export function executeCalcSafe(calc: string, params: Dict<never>, params2: Dict<never> | null = null): string | number {
  let mergedParams = Object.assign({}, params)
  if (params2) {
    mergedParams = { ...mergedParams, ...params2 }
  }
  try {
    const keys = Object.keys(mergedParams);
    const values = Object.values(mergedParams);
    const func = new Function(...keys, `return (${calc})`);
    console.debug(`executeCalc '${calc}' with`, keys, values, func);
    return func(values);
  } catch (e) {
    console.error("Errore nel calcolo:", e);
    return NaN;
  }
}